/**
 * Rollen-Optionen der Verantwortlichkeits-Eingabe: welche Rolle an welcher
 * Rechtsform wählbar ist und in welchem Abschnitt sie steht.
 *
 * Bewusst ohne React/DOM, damit die harte Filterung per Vitest prüfbar ist —
 * wie verantwortlichkeitDropdownLogik.ts.
 *
 * Fachlicher Rahmen (PO 15.08.2026):
 *  • Die Matrix filtert HART: Was nicht in `LegalFormVerantwortlichkeit` steht,
 *    ist an dieser Rechtsform nicht wählbar. Ein Einzelunternehmen hat keine
 *    Gesellschafter, Prokura kann nur ein Kaufmann erteilen (§ 48 Abs. 1 HGB).
 *  • Rollen der Kategorie WEITERE hängen nicht an der Rechtsform und bleiben
 *    immer wählbar. Sie stehen NICHT in einem Sammel-Abschnitt, sondern unter
 *    ihrer Zuständigkeit — Steuern, Finanzen, Fuhrpark, IT & Daten … — mit den
 *    Rollen darunter (PO 16.08.2026).
 *  • „Geschäftsführender Gesellschafter" ist KEINE Rolle, sondern die
 *    Kombination Leitung + Beteiligung → siehe utils/gfGesellschafter.ts.
 */

import type { VerantOption } from "./verantwortlichkeitDropdownLogik";
import { alsQuotenTyp, type QuotenTyp } from "../../utils/anteilFormat";
import type {
  RollenKategorie,
  Verantwortlichkeit,
  VerantwortlichkeitMatrixEintrag,
} from "../../services/verantwortlichkeitService";

/**
 * Anzeige-Reihenfolge der ORGAN-Abschnitte.
 *
 * Drei, nicht fünf: „Aufsicht" war eine eigene Kategorie, ist aber keine eigene
 * Art von Verantwortung — Aufsichtsrat, Kuratorium und Beirat gehören zur
 * Vertretung, wie sie auch in der Excel (Spalte E) stehen (PO 16.08.2026).
 * `AUFSICHT` bleibt im Typ erhalten, damit Bestandsdaten lesbar bleiben, wird
 * aber unter „Vertretung" einsortiert.
 *
 * `WEITERE` steht bewusst NICHT hier: Diese Rollen bekommen keinen gemeinsamen
 * Abschnitt, sondern je einen pro Zuständigkeit (siehe unten).
 */
export const KATEGORIE_REIHENFOLGE: RollenKategorie[] = [
  "LEITUNG",
  "GESELLSCHAFTER",
  "VERTRETUNG",
];

export const KATEGORIE_LABEL: Record<RollenKategorie, string> = {
  LEITUNG: "Unternehmensleitung",
  GESELLSCHAFTER: "Gesellschafter/Beteiligte",
  VERTRETUNG: "Vertretung",
  AUFSICHT: "Vertretung",
  // Sammelbegriff der Kategorie, KEINE Abschnittsüberschrift: Die Rollen dieser
  // Kategorie stehen unter ihrer jeweiligen Zuständigkeit („Steuern",
  // „Fuhrpark", „IT & Daten" …), nicht unter einem Sammeltitel.
  WEITERE: "Zuständigkeiten",
};

/**
 * Anzeige-Kategorie einer Rolle. Bestandsdaten und Matrix können weiterhin
 * `AUFSICHT` liefern; angezeigt wird sie als Vertretung.
 */
export const anzeigeKategorie = (k: RollenKategorie): RollenKategorie =>
  k === "AUFSICHT" ? "VERTRETUNG" : k;

/**
 * Feste Options-Werte. `__gf__` und `__ges__` bleiben Pseudo-Werte, weil sie im
 * Speicherpfad eigene Beziehungstypen sind (`role: 'gf' | 'gesellschafter'`) —
 * nur an ihnen hängen die Anteile. Alle übrigen Rollen werden als
 * Verantwortlichkeits-Bezeichnung gespeichert, ihr Options-Wert IST die
 * Bezeichnung.
 */
export const OPT_GF = "__gf__";
export const OPT_GES = "__ges__";
export const OPT_SONSTIGES = "__sonstiges__";

export const LABEL_SONSTIGES = "Sonstiges";
export const GRUPPE_FREITEXT = "Freitext";

/** Schlüssel der beiden Rollen, die auf die festen Optionen abgebildet werden. */
const SCHLUESSEL_GF = "GESCHAEFTSFUEHRER";
const SCHLUESSEL_GES = "GESELLSCHAFTER";
/**
 * „Geschäftsführender Gesellschafter" steht als Katalogrolle in der Matrix
 * (PR 8/9 haben sie mitgeseedet), wird hier aber NICHT angeboten: Der PO hat sie
 * am 15.08.2026 ausdrücklich zur abgeleiteten Anzeige erklärt. Wer Leitung und
 * Beteiligung wählt, bekommt sie als Chip.
 */
const SCHLUESSEL_GF_GES = "GF_GESELLSCHAFTER";

/**
 * Rollen, die auch ohne Schlüssel an eine natürliche Person gebunden sind.
 * Ehrenamtliche Vereinsämter (§ 27 BGB) tragen `istMenschImUnternehmen = false`,
 * weil sie unentgeltlich sind — eine Firma kann sie trotzdem nicht ausüben.
 *
 * Stellvertretender Vorsitz und Beisitz gehören dazu: beides Vorstandsämter wie
 * Schriftführer und Schatzmeister. Ehren- und Fördermitgliedschaft stehen
 * bewusst NICHT hier — eine Firma kann Fördermitglied eines Vereins sein.
 */
const NUR_NATUERLICHE_PERSON = new Set([
  "SCHRIFTFUEHRER",
  "SCHATZMEISTER",
  "PRAESIDENT",
  "STELLV_VORSITZENDER",
  "BEISITZER",
]);

/** Matrix-Schlüssel der Komplementär-GmbH (Migration 202608150003/4, nur KAPITAL_CO_KG). */
export const SCHLUESSEL_KOMPLEMENTAERIN_FIRMA = "KOMPLEMENTAERIN_FIRMA";

/** Auf Wunsch aus der Auswahl genommen (Bestandsentscheidung, unverändert). */
const VERSTECKTE_BEZEICHNUNGEN = new Set(["Finanzen"]);

/**
 * Kann eine FIRMA diese Rolle tragen?
 *
 * Ein eigenes Kennzeichen dafür gibt es im Katalog nicht. Belastbar ist
 * `istMenschImUnternehmen`: Es markiert die Rollen, die einen Menschen im
 * Betrieb voraussetzen — Geschäftsführer, Prokurist (§ 48 HGB), Vorstand. Eine
 * GmbH ist kein Mensch im Unternehmen, kann aber Komplementärin,
 * Gesellschafterin, Kommanditistin oder WEG-Verwalterin sein.
 *
 * Aufsichtsorgane fallen zusätzlich heraus: § 100 Abs. 1 AktG und § 29 Abs. 1
 * WEG verlangen ausdrücklich natürliche Personen.
 *
 * Bis 08/2026 war der Fall hart auf „Gesellschafter, intern" normalisiert
 * (`lockGesellschafterIntern`) — damit waren genau die Fälle unmöglich, die die
 * Excel-Matrix vorsieht: die Komplementär-GmbH und der persönlich haftende
 * Gesellschafter einer KGaA.
 */
export function firmaKannRolleTragen(r: {
  kategorie: RollenKategorie;
  istMenschImUnternehmen: boolean;
  schluessel: string | null;
}): boolean {
  if (r.kategorie === "AUFSICHT") return false;
  if (r.schluessel && NUR_NATUERLICHE_PERSON.has(r.schluessel)) return false;
  return !r.istMenschImUnternehmen;
}

/**
 * Art, in der eine reine ZUSTÄNDIGKEIT (Kategorie WEITERE) an dieser Verbindung
 * gilt (PO 20.08.2026):
 *
 *   Trägt eine FIRMA eine reine Zuständigkeit, ist sie der beauftragte
 *   Dienstleister — im Katalog heißt sie dann nicht „Steuern", sondern
 *   „Steuerberater". Bei einer PERSON entscheidet weiterhin der Umschalter: ein
 *   interner Datenschutzbeauftragter ist Angestellter, und an dieser
 *   Unterscheidung hängt der bAV/bKV-Haken (Migration 202608150003 setzt
 *   istMenschImUnternehmen = ("art" = 'intern')).
 *
 * Organrollen berührt die Regel nie — sie kommen aus der Matrix und sind immer
 * intern. Die Komplementär-GmbH wird also NICHT extern.
 */
export const zustaendigkeitsArt = (
  art: "intern" | "extern",
  gegenkontaktIstFirma: boolean,
): "intern" | "extern" => (gegenkontaktIstFirma ? "extern" : art);

/** Eine Rollen-Option samt ihrer Kategorie (für die Ableitung + die Gruppierung). */
export type RollenOption = VerantOption & {
  kategorie: RollenKategorie;
  /**
   * Welche Art Anteil diese Rolle traegt (PROZENT | EURO | BRUCHTEIL | KEINE).
   * Bis 08/2026 hing das Anteilsfeld allein an „Gesellschafter" — Kommanditistin
   * (§ 171 HGB, Euro), Wohnungseigentuemerin (Bruchteil) und Aktionaerin konnten
   * ihre Quote nicht erfassen, obwohl die Matrix den Typ liefert.
   */
  quotenTyp: QuotenTyp;
};

/** Options-Wert einer Matrix-Rolle: feste Rolle oder ihre Bezeichnung. */
export function optionsWert(m: Pick<VerantwortlichkeitMatrixEintrag, "schluessel" | "bezeichnung">): string {
  if (m.schluessel === SCHLUESSEL_GF) return OPT_GF;
  if (m.schluessel === SCHLUESSEL_GES) return OPT_GES;
  return m.bezeichnung;
}

/**
 * Baut die Optionsliste in fester Abschnitts-Reihenfolge.
 *
 * `matrixBekannt = false` heißt: Der Aufrufer kennt die Rechtsform nicht (die
 * drei Bereinigungsseiten laden sie nicht mit). Dann greift die harte Filterung
 * nicht, sondern es bleiben die beiden Rollen stehen, die es vor 08/2026 überall
 * gab — sonst risse mitten in einer laufenden Bereinigung die Auswahl ab.
 */
export function baueRollenOptionen({
  matrix,
  katalog,
  art,
  gegenkontaktIstFirma = false,
  matrixBekannt,
}: {
  matrix: VerantwortlichkeitMatrixEintrag[];
  katalog: Verantwortlichkeit[];
  art: "intern" | "extern";
  gegenkontaktIstFirma?: boolean;
  matrixBekannt: boolean;
}): RollenOption[] {
  const out: RollenOption[] = [];
  const nachWert = new Map<string, RollenOption>();

  const nimm = (o: RollenOption) => {
    const da = nachWert.get(o.value);
    if (da) {
      // Der Katalog fuehrt „Inhaber" und „Komplementaer" in ZWEI Kategorien
      // (202608150003), die Matrix „Komplementaerin (Gesellschaft)" zweimal
      // (202608150004) — die Quote steht jeweils nur an der
      // GESELLSCHAFTER-Zeile, einsortiert wird aber zuerst LEITUNG. Ein naiv
      // uebernommener Quotentyp waere hier KEINE, und das Feld verschwaende.
      // Die `kategorie` bleibt bewusst die der ERSTEN Zeile: Die Komplementaerin
      // ist Leitung und bekommt trotzdem ihr Prozentfeld.
      if (da.quotenTyp === "KEINE") da.quotenTyp = o.quotenTyp;
      return;
    }
    nachWert.set(o.value, o);
    out.push(o);
  };

  // ── Organrollen ──────────────────────────────────────────────────────────
  // Nur „intern": Ein Vorstand ist kein extern beauftragter Dienstleister; die
  // Katalogzeilen der Organrollen tragen deshalb durchweg art='intern'.
  if (art === "intern") {
    const organ: RollenOption[] = [];
    if (matrix.length) {
      for (const m of matrix) {
        if (m.schluessel === SCHLUESSEL_GF_GES) continue;
        if (gegenkontaktIstFirma && !firmaKannRolleTragen(m)) continue;
        // Die Komplementär-GmbH einer GmbH & Co. KG ist reine
        // Verwaltungsgesellschaft und wird nicht als Kontakt geführt (T11,
        // utils/komplementaerGmbh.ts). Die Rolle steht nur in der Matrix des
        // Profils KAPITAL_CO_KG — für eine Firma als Gegenüber gibt es sie nicht
        // mehr. Einer PERSON wird sie ebenfalls nicht angeboten: Sie meint
        // immer eine Gesellschaft, die Person ist „Komplementär" (T-448; die
        // Matrix führt die Rolle seit Migration 202611020201 auch dort).
        // Bestehende Zeilen zeigt `ergaenzeBestandsRollen` weiterhin an.
        if (m.schluessel === SCHLUESSEL_KOMPLEMENTAERIN_FIRMA) continue;
        // Zwischen zwei Firmen gibt es keine Gesellschafter-Kante mehr (User
        // 02.10.2026, Plan 2026-10-01 Paket A4): Hält eine Firma Anteile, ist sie
        // Mutterfirma oder hat eine Beteiligung — beides unter
        // „Verbindung zu Unternehmen". Eine bestehende Gesellschafter-Zeile
        // bleibt über `ergaenzeBestandsRollen` sichtbar.
        if (gegenkontaktIstFirma && m.schluessel === SCHLUESSEL_GES) continue;
        const wert = optionsWert(m);
        organ.push({
          value: wert,
          label: m.bezeichnung,
          bereich: m.bereich,
          gruppe: KATEGORIE_LABEL[m.kategorie] ?? m.bereich,
          kategorie: m.kategorie,
          // Der Gesellschafter behält sein Prozentfeld auch dann, wenn die
          // Matrix-Zeile keine Quote trägt: `LegalFormVerantwortlichkeit.quotenTyp`
          // steht per DEFAULT auf KEINE (202608150004), und ein leeres Feld beim
          // Nachpflegen einer Rechtsform wäre ein stiller Verlust. Die Matrix kann
          // ihn nur auf einen ANDEREN Typ heben. Der Geschäftsführer trägt nie eine.
          quotenTyp:
            wert === OPT_GF
              ? "KEINE"
              : wert === OPT_GES
                ? (alsQuotenTyp(m.quotenTyp) === "KEINE" ? "PROZENT" : alsQuotenTyp(m.quotenTyp))
                : alsQuotenTyp(m.quotenTyp),
        });
      }
    } else if (!matrixBekannt) {
      // Rückfall ohne bekannte Rechtsform — bewusst nur diese beiden.
      organ.push(
        { value: OPT_GF, label: "Geschäftsführer", bereich: KATEGORIE_LABEL.LEITUNG, gruppe: KATEGORIE_LABEL.LEITUNG, kategorie: "LEITUNG", quotenTyp: "KEINE" },
        { value: OPT_GES, label: "Gesellschafter", bereich: KATEGORIE_LABEL.GESELLSCHAFTER, gruppe: KATEGORIE_LABEL.GESELLSCHAFTER, kategorie: "GESELLSCHAFTER", quotenTyp: "PROZENT" },
      );
    }
    // Eine Firma trägt keine der beiden Rückfall-Rollen als Leitung; ohne
    // Rechtsform bleibt ihr nur die Beteiligung — das war vor 08/2026 der
    // gesperrte Zustand und bleibt hier das Minimum.
    const gefiltert =
      gegenkontaktIstFirma && !matrix.length
        ? organ.filter((o) => o.kategorie === "GESELLSCHAFTER" && o.value !== OPT_GES)
        : organ;

    // Über die ANZEIGE-Kategorie vergleichen: Matrix und Bestand liefern
    // weiterhin `AUFSICHT`; ohne die Abbildung fielen Aufsichtsrat, Kuratorium
    // und Beirat aus der Liste, weil `AUFSICHT` nicht mehr in der Reihenfolge steht.
    for (const kat of KATEGORIE_REIHENFOLGE) {
      for (const o of gefiltert) if (anzeigeKategorie(o.kategorie) === kat) nimm(o);
    }
  }

  // ── Zuständigkeiten ──────────────────────────────────────────────────────
  // Rechtsformunabhängig, deshalb ohne Matrix-Prüfung. Ein Abschnitt JE
  // Zuständigkeit: „Steuern", „Fuhrpark", „IT & Daten" … mit ihren Rollen
  // darunter (PO 16.08.2026) — ein gemeinsamer Sammeltitel sagte nichts, weil
  // die Zuständigkeit genau die Information ist, nach der man sucht. Die
  // Sortierung nach `bereich` bestimmt zugleich die Reihenfolge der Abschnitte:
  // `groupOptions` folgt dem ersten Auftreten.
  // Für eine FIRMA ist eine Zuständigkeit per Definition eine Beauftragung
  // (siehe zustaendigkeitsArt) — die Rollen kommen deshalb aus dem EXTERNEN
  // Katalog, obwohl der Organ-Block oben „intern" ist. Vorher schnitt der
  // art-Filter „Steuerberater" und „Wirtschaftsprüfer" weg, weil `art` bei einer
  // Firma immer auf „intern" läuft (der Umschalter fehlt dort) — angeboten wurde
  // ihr nur die interne Zeile „Steuern".
  const zustArt = zustaendigkeitsArt(art, gegenkontaktIstFirma);
  const weitere = katalog
    .filter((i) => (i.kategorie ?? "WEITERE") === "WEITERE")
    .filter((i) => (i.art === "extern") === (zustArt === "extern"))
    .filter((i) => i.aktiv !== false && !VERSTECKTE_BEZEICHNUNGEN.has(i.bezeichnung))
    .sort((a, b) => a.bereich.localeCompare(b.bereich, "de") || a.bezeichnung.localeCompare(b.bezeichnung, "de"));
  for (const i of weitere) {
    nimm({
      value: i.bezeichnung,
      label: i.bezeichnung,
      bereich: i.bereich,
      gruppe: i.bereich,
      kategorie: "WEITERE",
      quotenTyp: alsQuotenTyp(i.quotenTypDefault),
    });
  }

  // ── Freitext ─────────────────────────────────────────────────────────────
  nimm({
    value: OPT_SONSTIGES,
    label: LABEL_SONSTIGES,
    bereich: GRUPPE_FREITEXT,
    gruppe: GRUPPE_FREITEXT,
    kategorie: "WEITERE",
    // Eine frei eingetippte Rolle steht in keiner Matrix — ohne Quotentyp kein Feld.
    quotenTyp: "KEINE",
  });

  return out;
}

/** Abschnitt der Rollen, die es an dieser Rechtsform eigentlich nicht gibt. */
export const GRUPPE_BESTAND = "Aus dem Bestand";

/**
 * Ergänzt Rollen, die an der Verbindung BEREITS hängen, an dieser Rechtsform
 * aber nicht vorgesehen sind.
 *
 * Der Bestand wird nie hart geprüft (PO 15.08.2026): 350 der Organrollen
 * verstoßen gegen die Matrix, weil über die Hälfte der Firmen keine verwertbare
 * Rechtsform trägt. Ohne diesen Schritt verschwände so eine Rolle beim Öffnen
 * des Popups aus der Auswahl — und wäre beim nächsten Speichern gelöscht.
 *
 * Freitext-Rollen bleiben außen vor: Sie stehen in keinem Katalog und gehören
 * ins Feld „Sonstiges".
 */
export function ergaenzeBestandsRollen(
  options: RollenOption[],
  gewaehlt: { hatGf: boolean; hatGesellschafter: boolean; bezeichnungen: string[] },
  katalog: Verantwortlichkeit[],
): RollenOption[] {
  const vorhanden = new Set(options.map((o) => o.value));
  const nachBezeichnung = new Map(katalog.map((i) => [i.bezeichnung, i]));
  const extra: RollenOption[] = [];

  const bestand = (
    value: string,
    label: string,
    bereich: string,
    kategorie: RollenKategorie,
    quotenTyp: QuotenTyp = "KEINE",
  ) => extra.push({ value, label, bereich, gruppe: GRUPPE_BESTAND, kategorie, quotenTyp });

  if (gewaehlt.hatGf && !vorhanden.has(OPT_GF)) {
    bestand(OPT_GF, "Geschäftsführer", KATEGORIE_LABEL.LEITUNG, "LEITUNG");
  }
  if (gewaehlt.hatGesellschafter && !vorhanden.has(OPT_GES)) {
    // Wie im Rückfall oben: An der Beteiligung hängen die Anteile, auch wenn
    // diese Rechtsform die Rolle gar nicht führt (Bestandszeile).
    bestand(OPT_GES, "Gesellschafter", KATEGORIE_LABEL.GESELLSCHAFTER, "GESELLSCHAFTER", "PROZENT");
  }
  for (const b of gewaehlt.bezeichnungen) {
    if (vorhanden.has(b) || extra.some((o) => o.value === b)) continue;
    const eintrag = nachBezeichnung.get(b);
    if (!eintrag) continue; // Freitext → „Sonstiges"
    bestand(b, b, eintrag.bereich, eintrag.kategorie ?? "WEITERE", alsQuotenTyp(eintrag.quotenTypDefault));
  }

  return extra.length ? [...options, ...extra] : options;
}

/**
 * Leitungs-Option, die eine BESTANDS-Zeile „Gesellschafter +
 * function='geschäftsführend'" heute meint.
 *
 * Der geschäftsführende Gesellschafter steckt im Bestand in einer einzigen
 * Zeile. Ohne diese Auflösung fiele beim erneuten Speichern die Leitung weg —
 * an einer OHG gibt es keine Rolle „Geschäftsführer", dort heißt sie
 * „Vertretungsberechtigter Gesellschafter", an einer KG „Komplementär".
 */
export function leitungsOptionFuerBestand(options: RollenOption[]): string | undefined {
  const gf = options.find((o) => o.value === OPT_GF);
  if (gf) return gf.value;
  return options.find((o) => o.kategorie === "LEITUNG")?.value;
}
